import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Package, Plus, FileText, Trash2 } from "lucide-react";

/**
 * SundryItemsManager - Reusable component for managing sundry/manual line items
 * 
 * Props:
 * - sundryItems: array of sundry items
 * - onSundryItemsChange: callback when items change
 * - showAddButton: whether to show the add button (default: true)
 */
export const SundryItemsManager = ({ 
  sundryItems = [], 
  onSundryItemsChange,
  showAddButton = true 
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState({
    sku: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });

  const getSundryTotal = () => {
    return sundryItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
  };

  const handleAdd = () => {
    setForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingIndex(null);
    setShowDialog(true);
  };

  const handleEdit = (index) => {
    setForm(sundryItems[index]);
    setEditingIndex(index);
    setShowDialog(true);
  };

  const handleSave = () => {
    if (!form.description || parseFloat(form.quantity) <= 0) {
      return;
    }
    
    const newItem = {
      ...form,
      quantity: parseFloat(form.quantity) || 1,
      unit_price: parseFloat(form.unit_price) || 0,
      line_total: (parseFloat(form.quantity) || 1) * (parseFloat(form.unit_price) || 0)
    };
    
    let updated;
    if (editingIndex !== null) {
      updated = [...sundryItems];
      updated[editingIndex] = newItem;
    } else {
      updated = [...sundryItems, newItem];
    }
    
    onSundryItemsChange(updated);
    setShowDialog(false);
    setForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingIndex(null);
  };

  const handleDelete = (index) => {
    if (window.confirm('Remove this sundry item?')) {
      onSundryItemsChange(sundryItems.filter((_, i) => i !== index));
    }
  };

  return (
    <>
      {/* Add Button */}
      {showAddButton && (
        <Button 
          data-testid="add-sundry-button"
          onClick={handleAdd} 
          variant="outline"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Sundry Line
        </Button>
      )}

      {/* Sundry Items Table */}
      {sundryItems.length > 0 && (
        <Card className="print:hidden border-amber-200">
          <CardHeader className="bg-amber-50 py-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              Sundry Items
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-2">SKU</th>
                    <th className="py-2 px-2">Description</th>
                    <th className="py-2 px-2 text-right">Qty</th>
                    <th className="py-2 px-2 text-right">Unit Price</th>
                    <th className="py-2 px-2 text-right">Line Total</th>
                    <th className="py-2 px-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sundryItems.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-slate-50">
                      <td className="py-2 px-2 font-mono text-xs">{item.sku || '-'}</td>
                      <td className="py-2 px-2">{item.description}</td>
                      <td className="py-2 px-2 text-right">{item.quantity}</td>
                      <td className="py-2 px-2 text-right">£{item.unit_price.toFixed(2)}</td>
                      <td className="py-2 px-2 text-right font-medium">£{item.line_total.toFixed(2)}</td>
                      <td className="py-2 px-2 text-center">
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleEdit(index)}
                          >
                            <FileText className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(index)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-50 font-medium">
                    <td colSpan="4" className="py-2 px-2 text-right">Sundry Items Total:</td>
                    <td className="py-2 px-2 text-right">£{getSundryTotal().toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingIndex !== null ? 'Edit Sundry Item' : 'Add Sundry Item'}</DialogTitle>
            <DialogDescription>
              Add custom line items such as labour, cabling, commissioning, travel, etc.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sundry-sku">SKU (optional)</Label>
              <Input
                id="sundry-sku"
                placeholder="e.g. LABOUR-001"
                value={form.sku}
                onChange={(e) => setForm({...form, sku: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sundry-description">Description *</Label>
              <Input
                id="sundry-description"
                placeholder="e.g. Installation labour, Cabling, Travel"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sundry-quantity">Quantity *</Label>
                <Input
                  id="sundry-quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="1"
                  value={form.quantity}
                  onChange={(e) => setForm({...form, quantity: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sundry-price">Unit Price (£) *</Label>
                <Input
                  id="sundry-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.unit_price}
                  onChange={(e) => setForm({...form, unit_price: e.target.value})}
                />
              </div>
            </div>
            {form.quantity && form.unit_price && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Line Total:</span>
                  <span className="font-semibold">
                    £{((parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingIndex !== null ? 'Update' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * SundryTotalsRow - Component for displaying sundry totals in a table
 * 
 * Props:
 * - sundryItems: array of sundry items
 * - baseTotal: the base total to add sundry to
 * - colSpan: number of columns for the label cell
 * - showGrandTotal: whether to show grand total row
 */
export const SundryTotalsRow = ({ 
  sundryItems = [], 
  baseTotal = 0,
  colSpan = 5,
  showGrandTotal = true 
}) => {
  const sundryTotal = sundryItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
  
  if (sundryItems.length === 0) return null;
  
  return (
    <>
      <tr className="bg-amber-50">
        <td colSpan={colSpan} className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
        <td className="py-3 px-2 text-right font-bold text-amber-800">£{sundryTotal.toFixed(2)}</td>
        <td className="print:hidden"></td>
      </tr>
      {showGrandTotal && (
        <tr className="bg-green-100 border-t-2 border-green-400">
          <td colSpan={colSpan} className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
          <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">£{(baseTotal + sundryTotal).toFixed(2)}</td>
          <td className="print:hidden"></td>
        </tr>
      )}
    </>
  );
};

/**
 * Helper function to calculate sundry total
 */
export const calculateSundryTotal = (sundryItems = []) => {
  return sundryItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
};

export default SundryItemsManager;
